import { Component, MouseEvent, ReactNode, CSSProperties, EventHandler } from 'react'
import * as cn from 'classnames'
import * as styles from 'components/features/pfm/PeergroupForm/PeergroupForm.css'
import * as find from 'lodash/find'
import { Field, focus, reduxForm, stopSubmit } from 'redux-form'
import { allPeergroupFilters, getPeergroupFieldByFilter } from 'helpers/peergroupsHelpers'
import Button from 'components/features/pfm/Button/Button'
import TextFieldWrapped from 'components/atoms/inputs/TextFieldWrapped'
import LocationField from 'components/atoms/inputs/LocationField'
import OldSelectWrapped, { OldSelectOwnProps } from 'components/atoms/inputs/OldSelectWrapped'
import schema from 'schemas/peergroupFormSchema'
import { asyncValidator } from 'helpers/schemaHelpers'
import { ReduxFormProps, StoreDispatch } from 'interfaces/component'
import {
  PeergroupFormSelectField,
  PeergroupFormSearchField,
  PeergroupFormField,
  PeergroupFormType,
  PeergroupSelectFilterOptions,
  PeergroupFilterName,
  PeergroupRelation,
  Peergroup,
  RelationId,
} from 'interfaces/peergroups'
import { AnyDict } from 'interfaces'
import { getSaveButtonFormProps, onFormSubmit, shouldAsyncValidate } from 'helpers/formHelpers'
import { shortenNumber } from 'helpers/formatHelpers'
import { MINIMAL_GROUP_SIZE } from 'constants/peergroups'
import resolveErrorById, { TYPE_GROUP_NAME_EXISTS } from 'constants/errors'
import Tooltip from 'components/features/pfm/Tooltip/Tooltip'
import TextButton from 'components/atoms/buttons/TextButton'
import { addPeergroupFilterActionCreator, resetPeergroupFiltersActionCreator, setPeergroupFiltersPopupShown } from 'actions/peergroupActions'
import { OuterEventTrackerContext, OuterEventTrackerContextType } from 'components/utils/OuterEventTracker'
import withContext from 'hocs/withContext/withContext'

// TODO @pmyagkov deal with `initialValues`
export type PeergroupFormStateProps = {
  selectedFilters: PeergroupFormField[]
  isFiltersPopupShown: boolean
  isPeergroupSearching: boolean
  currentGroup: Peergroup | null
  relationId: RelationId | null
  selectOptions: PeergroupSelectFilterOptions
  formValues: AnyDict
  relations: PeergroupRelation[]
  // initialValues: AnyDict
  form: PeergroupFormType
}

export type PeergroupFormDispatchProps = {
  hideSidebarForm: () => void
  resetPeergroupFilters: typeof resetPeergroupFiltersActionCreator
  setFiltersPopupShown: typeof setPeergroupFiltersPopupShown
  addPeergroupFilter: typeof addPeergroupFilterActionCreator
  removePeergroupFilter: (form: PeergroupFormType, filter: PeergroupFilterName) => void
  focusFormField: typeof focus
}

export type PeergroupFormProps = ReduxFormProps & PeergroupFormStateProps & PeergroupFormDispatchProps & {
  form: PeergroupFormType
  initialValues: AnyDict
  outerEventTracker: OuterEventTrackerContextType
}

export type PeergroupFormState = {
  filtersPopupPlace: CSSProperties
}

const ADD_FILTER_COPY = 'Add New Filter'

const allFieldsValidator = (values: any, params: any) => asyncValidator(schema, params)(values)

function asyncValidate (values: AnyDict, dispatch: StoreDispatch, props: PeergroupFormProps): Promise<any> {
  const { relations, selectedFilters, form, relationId } = props
  const context = { selectedFilters: selectedFilters.map(({ name }) => name) }

  return allFieldsValidator(values, { context }).then(() => {
    const peergroupNames = relations.map(({ name }) => name)
    if (!peergroupNames.includes(values['name'])) {
      return
    }

    const groupBeingEdited = relations.find(({ id }) => id === relationId)
    if (groupBeingEdited && groupBeingEdited.name === values['name']) {
      return
    }
    dispatch(stopSubmit(form, { name: resolveErrorById(TYPE_GROUP_NAME_EXISTS) }))
  })
}

@reduxForm({
  asyncValidate: asyncValidate as any,
  onSubmit: onFormSubmit,
  shouldAsyncValidate,
  initialValues: {
    peergroup: {},
  },
  destroyOnUnmount: false,
})
class PeergroupForm extends Component<PeergroupFormProps, PeergroupFormState> {
  state: PeergroupFormState = {
    filtersPopupPlace: {
      top: 0,
    },
  }

  private filtersPopupNode: HTMLElement | null = null

  render () {
    const {
      isFiltersPopupShown,
      isPeergroupSearching,
      selectedFilters,
      formValues,
      handleSubmit,
    } = this.props

    const addFilterButton = selectedFilters.length < allPeergroupFilters.length ? (
      <div className={ cn(styles.fieldRow, styles.addFilterButtonWrapper) }>
        <button
          className={ styles.addFilterButton }
          type='button'

          onClick={ this.handleToggleFiltersPopup }
        >
          <div className={ cn(styles.addFilterIcon) } />
          <span>{ ADD_FILTER_COPY }</span>
        </button>
        { isFiltersPopupShown && this.renderFiltersPopup() }
      </div>
    ) : null

    const emptyFieldsExist = !formValues || selectedFilters.some(
      (option) => [undefined, null].includes(formValues.peergroup[option.name])
    )

    const { disabled, isSubmitting } = getSaveButtonFormProps(this.props)

    const disableSubmit = Boolean(
      disabled
        || isPeergroupSearching
        || selectedFilters.length < 1
        || emptyFieldsExist
        || this.isPeergroupTooSmall()
    )

    return (
      <form className={ styles.peergroupForm } onSubmit={ handleSubmit }>
        { this.renderGroupSizeBadge() }
        <div className={ styles.fieldRow }>
          <i className={ styles.fieldIcon } />
          <div className={ styles.fieldWrapper }>
            <Field
              props={{
                label: 'Group Name',
                type: 'text',
              }}
              component={ TextFieldWrapped }
              name='name'
              maxLength={ 30 }
            />
          </div>
        </div>
        { selectedFilters.map(this.renderFieldRow) }
        { addFilterButton }
        <div>
          <Button disabled={ disableSubmit } submit fullWidth isSubmitting={ isSubmitting } text='Save' />
          <div className={ styles.cancelButtonWrapper }>
            <TextButton
              theme='white'
              size='regular'
              onClick={ this.onHideSidebarForm }
            >
              Cancel
            </TextButton>
          </div>
        </div>
      </form>
    )
  }

  renderFilterChild = (filter: PeergroupFormField) => {
    const { selectedFilters } = this.props
    // TODO rewrite this to more efficient code
    const optionAlreadySelected = (option: string) => !!find(selectedFilters, { name: option })

    if (optionAlreadySelected(filter.name)) {
      return null
    }

    return (
      <div key={ filter.name } className={ styles.filterOption }>
        <a data-id={ filter.name } onClick={ () => this.onAddNewFilter(filter.name) }>{ filter.label }</a>
      </div>
    )
  }

  renderFiltersPopup = () => {
    const filtersPopupChild = (
      <div
        ref={ (node) => this.filtersPopupNode = node }
        className={ styles.filtersPopup }
        style={ this.state.filtersPopupPlace }
      >
        <h3 className={ styles.popupHeader }>{ ADD_FILTER_COPY }</h3>
        { allPeergroupFilters.map(this.renderFilterChild) }
      </div>
    )

    return filtersPopupChild
  }

  renderSelectField (field: PeergroupFormSelectField): ReactNode {
    const { name, label } = field
    const { selectOptions } = this.props

    const filterFieldProps: OldSelectOwnProps<string | number> = {
      label,
      options: selectOptions[name],
      theme: 'dark',
      allowOuterScroll: true,
      qaMarker: name,
      withBorder: false,
    }

    return (
      <Field
        name={ getPeergroupFieldByFilter(name) }
        props={ filterFieldProps }
        component={ OldSelectWrapped }
      />
    )
  }

  renderSearchField (field: PeergroupFormSearchField): ReactNode {
    const { name } = field

    return (
      <LocationField name={ getPeergroupFieldByFilter(name) } theme='dark' />
    )
  }

  renderFieldRow = (field: PeergroupFormField): ReactNode => {
    const { form, removePeergroupFilter } = this.props

    return (
      <div key={ field.name } className={ styles.fieldRow }>
        <button
          className={ styles.deleteFilterIcon }
          data-id='remove-filter'
          onClick={ () => removePeergroupFilter(form, field.name) }
        />
        <div className={ styles.fieldWrapper }>
          { field.type === 'select' ? this.renderSelectField(field) : this.renderSearchField(field) }
        </div>
      </div>
    )
  }

  renderGroupSizeBadge () {
    const { currentGroup, isPeergroupSearching } = this.props

    if (!currentGroup && !isPeergroupSearching) {
      return null
    }

    const groupSizeWrapperClass = cn(styles.groupSizeWrapper, {
      [styles.groupTooSmall]: this.isPeergroupTooSmall(),
      [styles.groupSearching]: isPeergroupSearching,
    })

    const badgeText = !currentGroup || isPeergroupSearching
      ? `Searching…`
      : (currentGroup.size === 0
        ? `No people found`
        : `${ shortenNumber(currentGroup.size) } people`
      )

    return (
      <div className={ styles.groupSizePlace }>
        <span className={ groupSizeWrapperClass }>
          <span className={ styles.groupSize }>{ badgeText }</span>
          <div className={ styles.tooltipContainer }>
            <Tooltip theme='danger' light wrap direction='down'>
              { `Custom groups must have at least ${ MINIMAL_GROUP_SIZE } people` }
            </Tooltip>
          </div>
        </span>
      </div>
    )
  }

  componentDidMount () {
    const { form, focusFormField } = this.props
    focusFormField(form, 'name')
  }

  componentDidUpdate (prevProps: PeergroupFormProps) {
    if (this.props.selectedFilters.length === 0) {
      this.props.resetPeergroupFilters()
    }
    if (this.props.selectedFilters.length > prevProps.selectedFilters.length) {
      this.focusNewlyAddedFilter()
    }
    if (!prevProps.isFiltersPopupShown && this.props.isFiltersPopupShown) {
      this.props.outerEventTracker.addOuterEventListener('tap click', this.closeFiltersPopup, this.filtersPopupNode)
    } else if (prevProps.isFiltersPopupShown && !this.props.isFiltersPopupShown) {
      this.props.outerEventTracker.removeOuterEventListener('tap click', this.closeFiltersPopup)
    }
  }

  componentWillUnmount () {
    this.props.outerEventTracker.removeOuterEventListener('tap click', this.closeFiltersPopup)
  }

  private focusNewlyAddedFilter = () => {
    const { selectedFilters, form, focusFormField } = this.props
    const filter = selectedFilters[selectedFilters.length - 1]
    focusFormField(form, getPeergroupFieldByFilter(filter.name))
  }

  private closeFiltersPopup = () => {
    this.props.setFiltersPopupShown(false)
  }

  private isPeergroupTooSmall () {
    const { currentGroup } = this.props
    return currentGroup && currentGroup.size < MINIMAL_GROUP_SIZE
  }

  private handleToggleFiltersPopup = () => {
    const { isFiltersPopupShown, setFiltersPopupShown } = this.props
    setFiltersPopupShown(!isFiltersPopupShown)
  }

  private onAddNewFilter = (filter: PeergroupFilterName) => {
    this.props.addPeergroupFilter(filter)
    this.closeFiltersPopup()
  }

  private onHideSidebarForm: EventHandler<MouseEvent<HTMLElement>> = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault()
    const { resetPeergroupFilters, hideSidebarForm } = this.props
    const reset = this.props.reset as () => void

    reset()
    resetPeergroupFilters()
    hideSidebarForm()
  }
}

export default withContext(OuterEventTrackerContext, 'outerEventTracker')(PeergroupForm)
